import { useEffect } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errMsg, fmtDT } from "../lib/api";
import { useLoad } from "./common";
import { POOL_LINKS } from "../lib/poolLinks";

const n = (v) => (v == null ? "millions" : v.toLocaleString());
const LINK_LABEL = { pending: "waiting", downloading: "downloading", loading: "loading", done: "loaded", failed: "failed" };

function Links({ links }) {
  if (!links?.length) return null;
  const done = links.filter((l) => l.status === "done").length;
  return (
    <div className="text-xs" data-testid="wash-links">
      <div className="font-semibold mb-1" data-testid="wash-links-done">{done} of {links.length} files loaded</div>
      <div className="max-h-56 overflow-auto border rounded">
        {links.map((l, i) => (
          <div key={l.url} className="flex justify-between gap-3 px-2 py-1 border-b last:border-0" data-testid={`wash-link-${i}`}>
            <span>{l.filename}</span>
            <span className={l.status === "failed" ? "text-red-700" : l.status === "done" ? "text-emerald-700" : "text-zinc-600"}>
              {LINK_LABEL[l.status]}{l.status === "loading" && l.progress != null ? ` ${l.progress}%` : ""}{l.inserted ? ` · ${l.inserted.toLocaleString()} new` : ""}{l.error ? ` · ${l.error}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Collecting({ w, act }) {
  return (
    <div className="space-y-3" data-testid="wash-collecting">
      <p className="text-sm">Started {fmtDT(w.started_at)}. Upload your new lists below now. Any number from the old pool that is on a new list becomes a brand-new lead.</p>
      <div className="flex flex-wrap gap-6 text-sm">
        <div>New leads uploaded: <b data-testid="wash-new-count">{n(w.new_count)}</b></div>
        <div>Old leads to wash out: <b data-testid="wash-old-count">{n(w.old_count)}</b></div>
      </div>
      {w.error && <div className="text-sm text-red-700" data-testid="wash-error">{w.error}</div>}
      <Links links={w.links} />
      {w.auto_wash && w.links?.some((l) => l.status !== "done" && l.status !== "failed") && <div className="text-sm text-emerald-700" data-testid="wash-auto-note">The old pool is washed out automatically once every file has loaded.</div>}
      <div className="flex flex-wrap gap-2">
        {!w.links?.some((l) => ["pending", "downloading", "loading"].includes(l.status)) && !(w.links?.length && w.links.every((l) => l.status === "done")) && <Button variant="outline" onClick={() => act("import-links", "Loading your files", `Load ${w.links?.length ? "the files that didn't load yet" : `your ${POOL_LINKS.length} files`}? Once every file is in, the old pool is permanently washed out automatically.`)} data-testid="wash-links-btn">{w.links?.length ? "Retry the files that didn't load" : `Load my ${POOL_LINKS.length} files from chat, wash when done`}</Button>}
        <Button disabled={w.links?.some((l) => ["pending", "downloading", "loading"].includes(l.status))} onClick={() => act("run", "Washing out the old pool…", `Delete ${n(w.old_count)} old leads for good? Unsent ones are pulled back from reps; ones already texted are deleted when they come back. Only do this after ALL your new lists have finished loading.`)} data-testid="wash-run-btn">Wash out old leads</Button>
        <Button variant="outline" onClick={() => act("cancel", "Pool replacement cancelled")} data-testid="wash-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}

function Result({ w }) {
  return (
    <div className="text-sm space-y-1" data-testid={`wash-${w.status}`}>
      <div>{w.status === "washing" ? "Washing out the old pool…" : `Old pool washed out ${fmtDT(w.finished_at)}.`}</div>
      <div className="text-zinc-600">Deleted <b data-testid="wash-deleted">{(w.deleted || 0).toLocaleString()}</b> · pulled back from reps <b data-testid="wash-pulled">{(w.pulled || 0).toLocaleString()}</b> · already texted, deleted when they come back <b data-testid="wash-kept">{(w.kept_texted || 0).toLocaleString()}</b></div>
    </div>
  );
}

export default function PoolWash() {
  const [w, reload] = useLoad(async () => (await api.get("/lead-wash")).data);
  const live = w && ["collecting", "washing"].includes(w.status);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(reload, w.status === "washing" ? 3000 : 15000);
    return () => clearInterval(t);
  }, [live, w?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, msg, ask) => {
    if (ask && !window.confirm(ask)) return;
    try {
      if (path === "start" || path === "start-links") {
        const s = (await api.post("/lead-wash/start")).data;
        if (path === "start-links") await api.post(`/lead-wash/${s.id}/import-links`, { urls: POOL_LINKS, auto_wash: true });
      } else {
        await api.post(`/lead-wash/${w.id}/${path}`, path === "import-links" ? { urls: POOL_LINKS, auto_wash: true } : undefined);
      }
      toast.success(msg); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-3 mb-8" data-testid="pool-wash">
      <h2 className="font-bold">Replace the whole pool</h2>
      {w?.status === "collecting" ? <Collecting w={w} act={act} /> : (
        <>
          {w && ["washing", "done"].includes(w.status) && <Result w={w} />}
          {w?.status !== "washing" && <>
            <p className="text-sm text-zinc-600">1. Press Start. 2. Upload all your new lists. 3. Press Wash out old leads: everything uploaded before Start is deleted for good (funded leads are kept).</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => act("start-links", `Loading your ${POOL_LINKS.length} files — the old pool is washed out when they're all in`, `Replace the whole pool with your ${POOL_LINKS.length} files (Mobile_0001–0035)? The live site loads them, then permanently deletes every older lead (funded kept).`)} data-testid="wash-start-links-btn">Replace pool with my {POOL_LINKS.length} files</Button>
              <Button variant="outline" onClick={() => act("start", "Started — upload your new lists now")} data-testid="wash-start-btn">Start and upload myself</Button>
            </div>
          </>}
        </>
      )}
    </div>
  );
}
